import type { Offer } from '@/api/types';
import type { StatsRow } from '@/api/tracker-types';

/** Offer creator bucket key; offers without a recorded creator share one bucket. */
export const UNASSIGNED_MANAGER = '__unassigned__';
export const NO_OFFER = '__no_offer__';

export interface ManagerTotals {
  clicks: number;
  unique_clicks: number;
  regs: number;
  deps: number;
  payout_cents: number;
}

export interface ManagerRow extends ManagerTotals {
  key: string;
  name: string;
  email: string | null;
  /** Offers this manager has created, all time. */
  offers: number;
  /** Offers created inside the report window (inclusive calendar days). */
  offersInPeriod: number;
  /** Offers that received at least one click in the window. */
  offersWithTraffic: number;
  /** Everyone else's offers combined (no-offer traffic excluded), for the comparison columns. */
  others: ManagerTotals;
}

const zero = (): ManagerTotals => ({ clicks: 0, unique_clicks: 0, regs: 0, deps: 0, payout_cents: 0 });
const add = (into: ManagerTotals, row: ManagerTotals) => {
  into.clicks += row.clicks;
  into.unique_clicks += row.unique_clicks || 0;
  into.regs += row.regs;
  into.deps += row.deps;
  into.payout_cents += row.payout_cents;
};

export function managerOf(offer: Pick<Offer, 'created_by_admin_id' | 'created_by_name' | 'created_by_email'>): { key: string; name: string; email: string | null } {
  if (!offer.created_by_admin_id && !offer.created_by_email && !offer.created_by_name) {
    return { key: UNASSIGNED_MANAGER, name: 'Unassigned', email: null };
  }
  return {
    key: offer.created_by_admin_id || offer.created_by_email || offer.created_by_name!,
    name: offer.created_by_name || offer.created_by_email || 'Unknown',
    email: offer.created_by_email,
  };
}

/**
 * Rolls per-offer tracker stats up to the person who created each offer.
 * Traffic that matched no offer is returned separately: it belongs to nobody
 * and must not tilt anyone's comparison.
 */
export function groupStatsByManager(
  rows: StatsRow[],
  offers: Offer[],
  window: { from: string; to: string },
): { managers: ManagerRow[]; noOffer: ManagerTotals | null; total: ManagerTotals } {
  const byId = new Map(offers.map((o) => [o.id, o]));
  const buckets = new Map<string, ManagerRow>();
  const bucketFor = (offer: Offer) => {
    const m = managerOf(offer);
    let b = buckets.get(m.key);
    if (!b) {
      b = { ...m, offers: 0, offersInPeriod: 0, offersWithTraffic: 0, others: zero(), ...zero() };
      buckets.set(m.key, b);
    }
    return b;
  };
  for (const offer of offers) {
    const b = bucketFor(offer);
    b.offers += 1;
    const day = offer.created_at.slice(0, 10);
    if (day >= window.from && day <= window.to) b.offersInPeriod += 1;
  }
  let noOffer: ManagerTotals | null = null;
  const total = zero();
  const attributed = zero();
  for (const row of rows) {
    add(total, row);
    const offer = row.group_value ? byId.get(row.group_value) : undefined;
    if (!offer) {
      // Deleted offers are as unattributable as flow fallbacks.
      noOffer ??= zero();
      add(noOffer, row);
      continue;
    }
    const b = bucketFor(offer);
    add(b, row);
    add(attributed, row);
    if (row.clicks > 0) b.offersWithTraffic += 1;
  }
  const managers = [...buckets.values()];
  for (const m of managers) {
    m.others = {
      clicks: attributed.clicks - m.clicks,
      unique_clicks: attributed.unique_clicks - m.unique_clicks,
      regs: attributed.regs - m.regs,
      deps: attributed.deps - m.deps,
      payout_cents: attributed.payout_cents - m.payout_cents,
    };
  }
  managers.sort((a, b) => b.payout_cents - a.payout_cents || b.deps - a.deps || b.clicks - a.clicks || a.name.localeCompare(b.name));
  return { managers, noOffer, total };
}

/** Percentage-point gap between two rates; null when either side has no denominator. */
export function ratePointDelta(num: number, den: number, otherNum: number, otherDen: number): number | null {
  if (!den || !otherDen) return null;
  return (num / den - otherNum / otherDen) * 100;
}

/** Relative gap of a per-unit value versus the others, as a fraction (0.2 = +20%). */
export function relativeDelta(num: number, den: number, otherNum: number, otherDen: number): number | null {
  if (!den || !otherDen) return null;
  const other = otherNum / otherDen;
  if (!other) return null;
  return num / den / other - 1;
}
